import type { SimulatorRequest, SimulatorProduct } from "common/types/Simulator";
import Typography, { ITypo } from "components/elements/Typography";
import MessageBox from "components/elements/MessageBox";
import NavTabComponent from "components/elements/NavTabComponent";
import { Choice, NumberField, Toggle, percent } from "./controls";
import { creditTypeLabels, deferTypeLabels, options, ownershipLabels, taxRegimeLabels } from "./labels";
import classes from "./classes.module.scss";

export default function Parameters({
	request,
	selected,
	update,
}: {
	request: SimulatorRequest;
	selected: SimulatorProduct[];
	update: (patch: Partial<SimulatorRequest>) => void;
}) {
	const credit = (patch: Partial<SimulatorRequest["credit"]>) => update({ credit: { ...request.credit, ...patch } });
	const tax = (patch: Partial<SimulatorRequest["tax"]>) => update({ tax: { ...request.tax, ...patch } });
	const durations = selected.length
		? selected[0]!.demembrementKeys
				.map((k) => k.duration)
				.filter((d) => selected.every((p) => p.demembrementKeys.some((k) => k.duration === d)))
				.sort((a, b) => a - b)
		: [];
	return (
		<NavTabComponent
			compact
			items={[
				{
					label: "Projet",
					children: (
						<div className={classes["fields"]}>
							<Choice
								label="Mode de détention"
								value={request.ownership}
								options={options(ownershipLabels)}
								onChange={(ownership) => update({ ownership })}
							/>
							{request.ownership !== "full" && (
								<>
									<Choice
										label="Durée du démembrement"
										value={String(request.ownershipYears)}
										options={durations.map((d) => [String(d), `${d} ans`])}
										onChange={(d) => update({ ownershipYears: Number(d) })}
									/>
									<Typography typo={ITypo.P_SMALL}>
										Le budget correspond au prix du droit acquis. Seules les clés renseignées pour toutes les SCPI sélectionnées sont
										utilisables.
									</Typography>
									{!durations.includes(request.ownershipYears) && (
										<MessageBox type="warning" text="Choisissez une durée commune disponible, ou adaptez votre sélection de SCPI." />
									)}
								</>
							)}
							<NumberField label="Horizon (années)" value={request.years} step={1} onChange={(years) => update({ years })} />
							<NumberField
								label="Baisse des dividendes / an (%)"
								percent
								value={request.dividendDecrease}
								onChange={(dividendDecrease) => update({ dividendDecrease })}
							/>
							<NumberField
								label="Évolution du prix / an (%)"
								percent
								value={request.appreciation}
								onChange={(appreciation) => update({ appreciation })}
							/>
							<Typography typo={ITypo.CAPTION}>Baisse relative du dividende, indépendante du prix de la part.</Typography>
						</div>
					),
				},
				{
					label: "Crédit",
					children: (
						<div className={classes["fields"]}>
							<Toggle label="Activer le crédit" checked={request.credit.enabled} onChange={(enabled) => credit({ enabled })} />
							{request.credit.enabled && (
								<>
									<Choice
										label="Type de crédit"
										value={request.credit.type}
										options={options(creditTypeLabels)}
										onChange={(type) => credit({ type })}
									/>
									<NumberField label="Montant emprunté (€)" value={request.credit.amount} onChange={(amount) => credit({ amount })} />
									<NumberField label="Taux nominal annuel (%)" percent value={request.credit.rate} onChange={(rate) => credit({ rate })} />
									<NumberField
										label="Durée du crédit, différé inclus (années)"
										step={1}
										value={request.credit.years}
										onChange={(years) => credit({ years })}
									/>
									<NumberField
										label="Assurance annuelle sur capital initial (%)"
										percent
										value={request.credit.insurance}
										onChange={(insurance) => credit({ insurance })}
									/>
									<NumberField
										label="Différé (mois)"
										step={1}
										value={request.credit.deferMonths}
										onChange={(deferMonths) => credit({ deferMonths })}
									/>
									{request.credit.deferMonths > 0 && (
										<Choice
											label="Type de différé"
											value={request.credit.deferType}
											options={options(deferTypeLabels)}
											onChange={(deferType) => credit({ deferType })}
										/>
									)}
									<Typography typo={ITypo.P_SMALL}>
										L’assurance est payée pendant le différé. Le capital restant dû est déduit de la valeur finale pour le TRI.
									</Typography>
								</>
							)}
						</div>
					),
				},
				{
					label: "Fiscalité",
					children: (
						<div className={classes["fields"]}>
							<Toggle label="Estimer la fiscalité (personne physique)" checked={request.tax.enabled} onChange={(enabled) => tax({ enabled })} />
							{request.tax.enabled && (
								<>
									<Choice
										label="Régime fiscal"
										value={request.tax.regime}
										options={options(taxRegimeLabels)}
										onChange={(regime) => tax({ regime })}
									/>
									<Choice
										label="Tranche marginale d’imposition"
										value={String(request.tax.marginalRate)}
										options={[0, 0.11, 0.3, 0.41, 0.45].map((t) => [String(t), percent(t)])}
										onChange={(v) => tax({ marginalRate: Number(v) })}
									/>
									<NumberField
										label="Prélèvements sociaux (%)"
										percent
										value={request.tax.socialRate}
										onChange={(socialRate) => tax({ socialRate })}
									/>
									<NumberField
										label="Part des revenus étrangers (%)"
										percent
										value={request.tax.foreignShare}
										onChange={(foreignShare) => tax({ foreignShare })}
									/>
									<NumberField
										label="Taux effectif sur revenus étrangers (%)"
										percent
										value={request.tax.foreignRate}
										onChange={(foreignRate) => tax({ foreignRate })}
									/>
									<Typography typo={ITypo.P_SMALL}>
										La répartition immobilière ne détermine pas automatiquement la fiscalité. Renseignez un taux étranger effectif global.
										Au réel, seuls les intérêts payés et l’assurance sont déduits ; les déficits ne sont pas reportés.
									</Typography>
									<Toggle label="Estimer le supplément d’IFI" checked={request.tax.ifi} onChange={(ifi) => tax({ ifi })} />
									{request.tax.ifi && (
										<NumberField
											label="Autre patrimoine immobilier net taxable (€)"
											value={request.tax.otherWealth}
											onChange={(otherWealth) => tax({ otherWealth })}
										/>
									)}
								</>
							)}
						</div>
					),
				},
			]}
		/>
	);
}
